// THE STANDARD: Accept Invite Page (light entry surface)
// New clients land here from their invite link: /invite/:token

import { useEffect, useState } from "react";
import { useLocation, useParams } from "wouter";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { Loader2, CheckCircle2, XCircle, Shield, ArrowRight, Clock } from "lucide-react";
import { toast } from "sonner";
import { STANDARD_LOGO_SRC } from '@/lib/brandAssets';
import '@/styles/light-entry.css';

export default function AcceptInvite() {
  const params = useParams<{ token: string }>();
  const token = params.token ?? "";
  const [, navigate] = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [redeemed, setRedeemed] = useState(false);

  const { data: invite, isLoading: validating, error: validateError } = trpc.invites.validate.useQuery(
    { token },
    { enabled: !!token, retry: false }
  );

  const utils = trpc.useUtils();
  const redeemMutation = trpc.invites.redeem.useMutation({
    onSuccess: async () => {
      setRedeemed(true);
      sessionStorage.removeItem('invite_token');
      toast.success("You are now connected to your coach. Welcome to The Standard.");
      await utils.billing.getSubscription.invalidate();
      setTimeout(() => navigate("/client"), 2000);
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  useEffect(() => {
    if (user && invite?.valid && !redeemed && !redeemMutation.isPending) {
      redeemMutation.mutate({ token });
    }
  }, [user, invite]);

  // Loading state
  if (authLoading || validating) {
    return (
      <div className="le-shell le-center">
        <div className="flex flex-col items-center gap-4" role="status">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#2f5d73' }} />
          <p className="le-eyebrow">
            Verifying Invite
          </p>
        </div>
      </div>
    );
  }

  // Invalid invite
  if (validateError || !invite) {
    return (
      <div className="le-shell le-center">
        <div className="le-frame le-stack">
          <div className="le-card le-card-pad text-center space-y-5">
            <div className="le-icon-badge mx-auto" data-tone="danger">
              <XCircle className="w-7 h-7" />
            </div>
            <div>
              <p className="le-eyebrow mb-2" style={{ color: '#9b3b31' }}>
                Invalid Link
              </p>
              <h1 className="le-title">
                Link Expired
              </h1>
            </div>
            <p className="le-copy">
              {validateError?.message ?? "This invite link is invalid, expired, or has already been used."}
            </p>
            <p className="le-small">Contact your coach for a new invite link.</p>
          </div>
        </div>
      </div>
    );
  }

  // Redeemed: success state
  if (redeemed) {
    return (
      <div className="le-shell le-center">
        <div className="le-frame le-stack">
          <div className="le-card le-card-pad text-center space-y-5">
            <div className="le-icon-badge mx-auto" data-tone="success">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <div>
              <p className="le-eyebrow mb-2" style={{ color: '#2f6b4a' }}>
                Access Granted
              </p>
              <h1 className="le-title">
                You Are In
              </h1>
            </div>
            <p className="le-copy" role="status">Redirecting you to The Standard...</p>
            <Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: '#2f5d73' }} />
          </div>
        </div>
      </div>
    );
  }

  // Compute expiry warning: show if link expires within 48 hours
  const hoursUntilExpiry = invite.expiresAt
    ? (new Date(invite.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60)
    : null;
  const showExpiryWarning = hoursUntilExpiry !== null && hoursUntilExpiry <= 48 && hoursUntilExpiry > 0;

  // Main invite view
  return (
    <div className="le-shell le-center">
      <div className="le-frame le-stack">

        {/* Expiry warning banner */}
        {showExpiryWarning && (
          <div className="le-alert le-alert-note flex items-start gap-3" role="status">
            <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#7d5f2c' }} />
            <div>
              <p className="le-eyebrow">
                Link Expires Soon
              </p>
              <p className="le-small mt-0.5" style={{ color: '#17151a' }}>
                This invite expires in {Math.ceil(hoursUntilExpiry!)} hour{Math.ceil(hoursUntilExpiry!) !== 1 ? 's' : ''}. Accept now before it runs out.
              </p>
            </div>
          </div>
        )}

        {/* Logo + Brand */}
        <div className="le-brand-plate flex-col gap-3">
          <img src={STANDARD_LOGO_SRC} alt="The Standard" className="le-brand-logo" />
          <p className="le-eyebrow" style={{ color: '#d2ad70' }}>
            Private Coaching Platform
          </p>
        </div>

        {/* Invite Card */}
        <div className="le-card le-card-pad space-y-4">
          <div className="flex items-center gap-3">
            <div className="le-icon-badge le-icon-badge-sm">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <p className="le-eyebrow">
                You Have Been Invited
              </p>
              <p className="le-h3 mt-0.5">
                Coach Fe
              </p>
            </div>
          </div>

          {invite.label && (
            <p className="le-strong text-sm">{invite.label}</p>
          )}

          {invite.referredByName && (
            <div className="le-inset flex flex-wrap items-center gap-2 !py-2.5">
              <span className="le-eyebrow">
                Referred by
              </span>
              <span className="le-strong text-sm">{invite.referredByName}</span>
            </div>
          )}

          <p className="le-copy">
            Your coach has invited you to join The Standard. A private coaching platform built for people who are done making excuses and ready to do the work.
          </p>

          <div className="pt-3 border-t border-[#d9d4ca]">
            <p className="le-small">
              Fitness. Nutrition. Accountability. Communication. All in one place.
            </p>
          </div>
        </div>

        {/* CTA */}
        {!user ? (
          <div className="space-y-3">
            <button
              className="le-btn le-btn-primary"
              onClick={() => {
                sessionStorage.setItem("invite_token", token);
                window.location.href = `/login`;
              }}
            >
              Accept Invite and Log In
              <ArrowRight className="w-4 h-4" />
            </button>
            <p className="le-small le-center-text">
              You will be redirected back here after logging in.
            </p>
          </div>
        ) : (
          <button
            className="le-btn le-btn-primary"
            onClick={() => redeemMutation.mutate({ token })}
            disabled={redeemMutation.isPending}
          >
            {redeemMutation.isPending ? (
              <><Loader2 className="w-4 h-4 animate-spin" />Connecting...</>
            ) : (
              <>Accept and Enter The Standard <ArrowRight className="w-4 h-4" /></>
            )}
          </button>
        )}

        {/* Footer */}
        <p className="le-footer-note pt-2">
          The Standard &bull; Hard Work. Commitment. Grit.
        </p>
      </div>
    </div>
  );
}
